import {
  ArrowUturnLeftIcon,
  BookmarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";
import api from "@src/api/api";
import useUser from "@src/api/user/useUser";
import ButtonsBar from "@src/components/elements/form/ButtonsBar";
import Button from "@src/components/elements/input/Button";
import StarsRating from "@src/components/elements/input/StarsRating";
import JobCard from "@src/pages/jobs/components/JobCard";
import JobCarousel from "@src/pages/jobs/components/JobCarousel";
import JobReviews from "@src/pages/jobs/components/JobReviews";
import JobStarReviewBox from "@src/pages/jobs/components/JobStarReviewBox";
import MessageFormButton from "@src/pages/jobs/details/actionbar/MessageFormButton";
import ImageGrid from "@src/pages/marketplace/shop/item/ImageGrid";
import LegacyPost from "@src/pages/news/posts/LegacyPost";
import getName from "@src/utils/getName";
import { MediaData } from "@src/utils/mediaData";
import { JobType } from "@src/utils/types/JobType";
import { Media } from "@src/utils/types/Media";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { SwiperSlide } from "swiper/react";
import tw from "twin.macro";

type Props = {
  preloadedJob?: JobType;
};
type Response = {
  data: JobType[];
};

const JobsDetails = ({ preloadedJob }: Props) => {
  const isPreview = preloadedJob !== undefined;
  const { id } = useParams();
  const { t } = useTranslation();
  const [textMinimized, setTextMinimized] = useState(true);
  const navigate = useNavigate();

  const jobs = useQuery<Response, Error>(["jobs"], async () => {
    const { data } = await api.get("/api/v1/jobs");
    return data;
  });

  const jobQuery = useQuery<JobType, Error>(
    [`job-${id}`],
    async () => {
      if (id === undefined) return;
      const res = await api.get(`/api/v1/jobs/${id}`);
      return res.data.data;
    },
    { enabled: !isPreview, initialData: preloadedJob },
  );

  const media: Array<Media> = MediaData;

  const user = useUser(jobQuery.data?.user_id ?? "");

  if (id === undefined && !isPreview) return <div>Job not found</div>;

  if (!jobQuery.isSuccess || !user.isSuccess) return <div>Loading...</div>;

  const job = isPreview ? preloadedJob : jobQuery.data;
  return (
    <div css={!isPreview && tw`xl:px-[180px]`}>
      <div className="flex flex-row items-start justify-between max-xl:px-4">
        <div className="text-xl">{job.title}</div>
        {!isPreview && (
          <ArrowUturnLeftIcon
            className="size-6 cursor-pointer"
            onClick={() => {
              navigate(-1);
            }}
          />
        )}
      </div>
      <div className="w-full flex-col justify-between gap-2 text-sm max-xl:px-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <div className="inline-flex items-center gap-2 whitespace-nowrap">
            <StarsRating rating={5} />
            <div className="text-xs text-gray-600">
              {t("main.shop.details.starsRating", { count: 100 })}
            </div>
          </div>
          <div className="inline-flex w-full gap-2 text-xs">
            <div className="inline-flex items-center gap-0.5">
              <EyeIcon className="size-3 text-darkblue" />
              <div className="text-gray-500">{"355".padStart(6, "0")}</div>
            </div>
            <div className="inline-flex items-center gap-0.5">
              <BookmarkIcon className="size-3 text-darkblue" />
              <div className="text-gray-500">{"81".padStart(6, "0")}</div>
            </div>
            <div className="inline-flex items-center gap-0.5">
              <HeartIcon className="size-3 text-darkred" />
              <div className="text-gray-500">{"107".toString().padStart(6, "0")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 divide-y divide-black text-darkblue">
        <div className="relative flex h-fit flex-col gap-4 xl:flex-row">
          <div className="left-0 top-0 flex h-fit shrink-0 flex-col gap-2 xl:sticky xl:w-2/5">
            <ImageGrid
              images={media}
              likeButton={() => {
                return;
              }}
              shareButton={() => {
                return;
              }}
            />
            <div className="max-xl:px-4">
              <ButtonsBar
                spread
                printButton={{
                  printUrl: "",
                }}
                likeButton={{
                  likeUrl: "/api/v1/marketplace/products/" + id + "/like",
                  unlikeUrl: "/api/v1/marketplace/products/" + id + "/unlike",
                  liked: false,
                  like_count: 0,
                }}
                viewButton={{ view_count: 0 }}
                chatButton={{
                  chatUrl: "/api/v1/chats/create/" + "",
                }}
                shareButton={{
                  title: "Profil",
                  body: "https://www.tradefoox.com/marketplace/" + id,
                }}
                itemId={id ?? ""}
                itemType="job"
              />
            </div>
          </div>
          <div className="flex w-full flex-col justify-between gap-2 p-4 text-sm max-xl:px-4 xl:flex-row">
            <div className="flex flex-col">
              <div className="text-sm font-semibold">
                {t("main.navLinks.jobsPublished.description")}:
              </div>
              <div css={[tw`text-sm`, textMinimized && tw`line-clamp-5`]}>
                {job.old_id ? <LegacyPost content={job.description} /> : job.description}
              </div>
              <button
                className="mt-1 flex w-36 items-center justify-between rounded-full border border-gray-500 px-2 py-1 text-xs"
                onClick={() => setTextMinimized(!textMinimized)}
              >
                {textMinimized ? t("buttons.showMore") : t("buttons.showLess")}
                <div className="shrink-0">
                  {textMinimized ? (
                    <ChevronDownIcon className="size-4" />
                  ) : (
                    <ChevronUpIcon className="size-4" />
                  )}
                </div>
              </button>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-8  p-4 text-gray-800 md:p-0 xl:w-[250px]">
            <div className="flex h-fit flex-col rounded-lg border border-gray-200 bg-gray-100 px-2 py-1 text-sm text-gray-800">
              <div className="mb-1.5 whitespace-nowrap border-b border-gray-300 pb-1.5 font-semibold">
                {t("main.navLinks.jobsPublished.general")}
              </div>
              <div className="grid max-w-full items-center">
                <div className="flex">
                  <img
                    src={"/images/icons/briefcase.svg"}
                    className="mr-1 mt-1 flex size-[15px]"
                    alt="icon"
                  />
                  <div className="font-bold">{t("main.navLinks.jobsPublished.occupation")}:</div>
                </div>

                <div className="ml-5">{job.occupation}</div>
              </div>

              <div className="grid max-w-full grid-cols-[20px,1fr] items-center">
                <div className="">
                  <img
                    src={"/images/icons/calender-days.svg"}
                    className="mr-1 flex size-[15px]"
                    alt="icon"
                  />
                </div>

                <div className="font-bold">{t("main.navLinks.jobsPublished.jobStartDate")}:</div>
                <div className="ml-5">{new Date(job.start_date).toLocaleDateString("de-DE")}</div>
              </div>

              <div className="grid max-w-full grid-cols-[20px,1fr] items-center">
                <div className="">
                  <img
                    src={"/images/icons/circle-badge.svg"}
                    className="mr-1 flex size-[15px]"
                    alt="icon"
                  />
                </div>
                <div className="font-bold">
                  {t("main.navLinks.jobsPublished.jobSocialSecurityRequired")}:
                </div>
                <div></div>
                {job.social_security_required ? "ja" : "nein"}
              </div>
              <div>
                <div className="grid max-w-full grid-cols-[20px,1fr] items-center">
                  <img
                    src={"/images/icons/folder.svg"}
                    className="mr-1 flex size-[15px]"
                    alt="icon"
                  />
                  <div className="font-bold">
                    {t("main.navLinks.jobsPublished.applicationDetails")}:
                  </div>
                  <div className=""></div>
                </div>
                <div className="grid max-w-full items-center">
                  <div className="flex">
                    <img
                      src={"/images/icons/map-pin.svg"}
                      className="mr-1 mt-1 flex size-[15px]"
                      alt="icon"
                    />
                    <div className="font-bold">
                      {" "}
                      {t("main.navLinks.jobsPublished.typeOfApplication")}:
                    </div>
                  </div>

                  <div className="ml-5">{job.application_type}</div>
                </div>
              </div>
            </div>
            <div className="flex h-fit flex-col rounded-lg border border-gray-200 bg-gray-100 px-2 py-1 text-sm text-gray-800">
              <div className="mb-1.5 whitespace-nowrap border-b border-gray-300 pb-1.5 font-semibold">
                {t("main.navLinks.jobsPublished.contactInformation")}
              </div>
              <div className="grid max-w-full grid-cols-[20px,1fr] items-center">
                <img src={"/images/icons/clock.svg"} className="mr-1 flex size-[15px]" alt="icon" />
                <div className="font-bold">{t("main.navLinks.jobsPublished.workingHours")}:</div>
                <div className="ml-5">
                  {job.weekly_hours}
                  <span className="ml-1">
                    {t("main.navLinks.jobsPublished.jobDataWeeklyHours")}
                  </span>
                </div>
              </div>

              <div className="grid max-w-full grid-cols-[20px,1fr] items-center">
                <img
                  src={"/images/icons/currency-euro.svg"}
                  className="mr-1 flex size-[15px]"
                  alt="icon"
                />
                <div className="font-bold">{t("main.navLinks.jobsPublished.remuneration")}: </div>
                <div className="ml-5"> {job.compensation}</div>
              </div>

              <div className="grid max-w-full grid-cols-[20px,1fr] items-center">
                <img
                  src={"/images/icons/clipboard-document-list.svg"}
                  className="mr-1 flex size-[15px]"
                  alt="icon"
                />{" "}
                <div className="font-bold">{t("main.navLinks.jobsPublished.wageAgreement")}:</div>
                <div className="ml-5">{job.standard_wage ? "ja" : "nein"}</div>
              </div>

              <div className="grid max-w-full grid-cols-[20px,1fr] items-center">
                <img
                  src={"/images/icons/chart-bar.svg"}
                  className="mr-1 flex size-[15px]"
                  alt="icon"
                />
                <div className="font-bold">{t("main.navLinks.jobsPublished.limitation")}:</div>
                <div className="ml-5">{job.limitation ? "ja" : "nein"}</div>
              </div>

              <div className="grid max-w-full items-center">
                <div className="flex">
                  <img
                    src={"/images/icons/map-pin.svg"}
                    className="mr-1 mt-1 flex size-[15px]"
                    alt="icon"
                  />
                  <div className="font-bold">{t("main.navLinks.jobsPublished.location")}:</div>
                </div>

                <div className="ml-5">{job.working_place}</div>
              </div>
            </div>
            <Button variant={Button.Variants.Transparent} size={Button.Sizes.Small}>
              <MessageFormButton />
            </Button>
          </div>
        </div>
        <div className="pt-2 max-xl:px-4">
          <div className="flex h-fit flex-col rounded-lg border border-gray-200 px-2 py-1 text-sm">
            <div className="mb-1.5 whitespace-nowrap border-b border-gray-300 pb-1.5 font-semibold">
              {t("main.navLinks.jobsPublished.contactInformation")}
            </div>
            <div className="flex flex-wrap gap-1">
              <div>{getName(user.data) || job.company}</div>
            </div>

            <div className="flex flex-wrap gap-1">{user.data?.email}</div>
          </div>
        </div>
        <div className="flex flex-col gap-2 p-4">
          <div className="flex flex-col gap-8 xl:flex-row">
            <div className="inline-flex gap-8 text-sm">
              <JobStarReviewBox />
            </div>
            <div className="w-full">
              <JobReviews />
              <div className="text-right text-sm font-semibold text-lightblue hover:underline">
                <NavLink to={"/jobs/reviews/" + id}>
                  {t("main.navLinks.jobsPublished.seeMoreReviews")}
                </NavLink>
              </div>
            </div>
          </div>
        </div>
        <div className="pt-2 max-xl:px-4">
          <div className="flex flex-row">
            <span className="w-full">
              {t("main.navLinks.jobsPublished.MoreJobsFrom")}:{" "}
              <NavLink to={"/profile/" + user.data.id + "/jobs"}>
                <a className="truncate text-darkred">{getName(user.data)}</a>
              </NavLink>
            </span>
          </div>

          <div className="w-full">
            <JobCarousel spaceBetween={20} slidesPerView={5} mousewheel={true}>
              {jobs.data &&
                jobs.data.data.map(job => (
                  <>
                    <SwiperSlide className="w-full p-2">
                      <JobCard job={job} />
                    </SwiperSlide>
                  </>
                ))}
            </JobCarousel>
          </div>
        </div>
        <div className="pt-2 max-xl:px-4">
          {t("main.navLinks.jobsPublished.similarJobs")}
          <div className="w-full">
            <JobCarousel spaceBetween={20} slidesPerView={5} mousewheel={true}>
              {jobs.data &&
                jobs.data.data.map(job => (
                  <>
                    <SwiperSlide className="w-full p-2">
                      <JobCard job={job} />
                    </SwiperSlide>
                  </>
                ))}
            </JobCarousel>
          </div>
        </div>

        <div className="mb-5 flex flex-wrap justify-end gap-4 pt-2 font-normal max-xl:px-4">
          <Button
            onClick={() => {
              console.log("Bewertungen");
            }}
            variant={Button.Variants.Transparent}
            size={Button.Sizes.Small}
            fitWidth
          >
            {t("main.navLinks.jobsPublished.multipleReviews")}
          </Button>
          <Button
            onClick={() => {
              console.log("Veröffentlichen");
            }}
            variant={Button.Variants.Transparent}
            size={Button.Sizes.Small}
            fitWidth
          >
            {t("buttons.publish")}
          </Button>
          <Button variant={Button.Variants.Transparent} size={Button.Sizes.Small} fitWidth>
            <NavLink to={`/profile/${job?.user_id}`} target="_blank">
              {t("main.navLinks.jobsPublished.toEmployee")}
            </NavLink>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobsDetails;
